// components/MenuItem.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useSetup } from "@/contexts/SetupContext";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LockIcon, Loader2 } from "lucide-react";
import { MenuItem } from "@/types/menu";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";

export const MenuListItem: React.FC<MenuItem> = ({
  to,
  icon,
  text,
  onClick,
  superUserOnly,
  id,
  className,
  disabled,
  "data-testid": dataTestId,
  badge,
}) => {
  const navigate = useNavigate();
  const { isPathAllowed, isLoading } = useSetup();
  const { user } = useAuth();

  // If superUserOnly is true and user is not a superuser, don't render the item
  if (superUserOnly && user && !isSuperUser(user)) {
    return null;
  }

  if (!to && onClick) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn("w-full justify-between", className)}
        onClick={onClick}
        id={id}
        disabled={disabled}
        data-testid={dataTestId}
      >
        <div className="flex items-center">
          {icon}
          <span className="ml-3">{text}</span>
        </div>
        {badge}
      </Button>
    );
  }

  const isAllowed = !to || isPathAllowed(to);

  if (isLoading) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn("w-full justify-between", className)}
        disabled
        id={id}
        data-testid={dataTestId}
      >
        <div className="flex items-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="ml-3">{text}</span>
        </div>
        {badge}
      </Button>
    );
  }

  // Handle disabled prop
  if (disabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground opacity-50"
              disabled
              id={id}
              data-testid={dataTestId}
            >
              {icon}
              <span className="ml-3">{text}</span>
              {badge}
              <LockIcon className="ml-auto h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px]">
            <p className="text-xs">
              Complete your business details to access this section
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (!isAllowed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              disabled
              id={id}
              data-testid={dataTestId}
            >
              {icon}
              <span className="ml-3">{text}</span>
              {badge}
              <LockIcon className="ml-auto h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px]">
            <p className="text-xs">
              Complete the setup checklist to unlock this section
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn("w-full justify-between", className)}
      onClick={() => to && navigate(to)}
      id={id}
      data-testid={dataTestId}
    >
      <div className="flex items-center">
        {icon}
        <span className="ml-3">{text}</span>
      </div>
      {badge}
    </Button>
  );
};
